/**
 * The default palette, injected into `:root` as INLINE STYLE by `ThemeSync`.
 *
 * WHY THIS FILE IS DANGEROUS, AND WHY IT NOW MIRRORS theme.css
 * These values are written with `root.style.setProperty(...)`, and an inline
 * style beats every stylesheet rule no matter where it sits in the cascade. So
 * whatever is here IS `--color-primary` at runtime — `theme.css` never gets a
 * vote.
 *
 * It still held the ORIGINAL blueprint palette: Midnight Blue #0B1F3A and
 * Electric Indigo #635BFF, the scheme the product moved away from long ago.
 * The result was invisible in the stylesheet and obvious on screen: every rule
 * written against `--color-primary` — the chat's send button, headings, focus
 * rings — rendered NAVY while `theme.css` declared olive. Measured in a live
 * browser: the armed send button computed to `rgb(11, 31, 58)`.
 *
 * These are now the same values as the `:root` block in
 * `assets/styles/theme.css`, which is the design system of record. A tenant's
 * own `theme_primary` still overrides `primary` — that is the point of the
 * injector — but the DEFAULT must be what the product actually looks like.
 * Change a colour here and in theme.css together, or the two disagree again
 * and only the browser will tell you.
 */
export const baseTheme = {
  colors: {
    background: "#f0eee6",   // --color-page
    surface: "#faf9f4",      // --color-surface
    text: "#14150f",         // --color-text
    primary: "#4b5320",      // --color-primary (dark olive)
    accent: "#cdf000",       // --color-accent (electric chartreuse)
    accentStrong: "#a8c400",
    accentSoft: "rgba(205, 240, 0, 0.22)",
    success: "#3f7a28",      // --color-success
    danger: "#c8321e",       // --color-danger
    coral: "#c8321e",
    mint: "#eef3dc",
    border: "rgba(20, 21, 15, 0.12)",  // --color-border
    muted: "#55564c"                   // --color-muted
  }
};

const normalizeHex = (color: string) => (color.startsWith("#") ? color.slice(1) : color);

const hexToRgb = (color: string) => {
  const normalized = normalizeHex(color);
  if (!/^([\da-f]{6})$/i.test(normalized)) {
    return null;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return { r, g, b } as const;
};

export const hexToRgba = (color: string, alpha: number) => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const shadeHexColor = (color: string, percent: number) => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return color;
  }
  const amount = Math.abs(percent) / 100;
  const target = percent < 0 ? 0 : 255;
  const r = Math.round(rgb.r + (target - rgb.r) * amount);
  const g = Math.round(rgb.g + (target - rgb.g) * amount);
  const b = Math.round(rgb.b + (target - rgb.b) * amount);
  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
